import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserX } from 'lucide-react';
import { usePlayerIdentification } from '@/hooks/usePlayerIdentification';
import { SegmentIdentifier } from './SegmentIdentifier';
import { computeDisabledPlayers } from '@/lib/transforms';
import { NOT_PRESENT } from '@/lib/constants';
import type {
  SegmentStatistics,
  PlayerSlot,
  PlayersCorrespondance,
} from '@/types/statistics';

interface PlayerIdentificationFlowProps {
  segments: SegmentStatistics[];
  onComplete: (identifications: Record<number, PlayerSlot>) => void;
  preIdentifications?: Record<number, PlayerSlot>;
  playersCorrespondance?: PlayersCorrespondance;
  currentUserUuid?: string;
}

export const PlayerIdentificationFlow: React.FC<
  PlayerIdentificationFlowProps
> = ({
  segments,
  onComplete,
  preIdentifications,
  playersCorrespondance,
  currentUserUuid,
}) => {
  const { t } = useTranslation('identification');
  const totalSegments = segments.length;
  const { identify, goTo, identifications, currentSegmentIndex } =
    usePlayerIdentification(totalSegments, preIdentifications);

  const [visible, setVisible] = useState(true);
  const [animatingIndex, setAnimatingIndex] = useState(currentSegmentIndex);

  // Indices of segments that need manual identification (not pre-identified)
  const unidentifiedIndices = useMemo(() => {
    const indices: number[] = [];
    for (let i = 0; i < totalSegments; i++) {
      if (preIdentifications?.[i] === undefined) {
        indices.push(i);
      }
    }
    return indices;
  }, [totalSegments, preIdentifications]);

  // Current position within unidentified segments only
  const currentUnidentifiedPos =
    unidentifiedIndices.indexOf(currentSegmentIndex);
  const totalUnidentified = unidentifiedIndices.length;

  // Disabled players for each segment, keyed by the segment's block_id
  const disabledPlayersPerSegment = useMemo(() => {
    if (!playersCorrespondance || !currentUserUuid) return {};
    const result: Record<number, Set<PlayerSlot>> = {};
    segments.forEach((seg, i) => {
      result[i] = computeDisabledPlayers(
        seg.metadata.block_id,
        playersCorrespondance,
        currentUserUuid,
      );
    });
    return result;
  }, [segments, playersCorrespondance, currentUserUuid]);

  // Animate segment transitions
  useEffect(() => {
    if (animatingIndex !== currentSegmentIndex) {
      setVisible(false);
      const t = setTimeout(() => {
        setAnimatingIndex(currentSegmentIndex);
        setVisible(true);
      }, 150);
      return () => clearTimeout(t);
    }
  }, [currentSegmentIndex, animatingIndex]);

  const currentSegment = segments[animatingIndex];
  const selectedPlayerId = identifications[animatingIndex] ?? null;
  const isNotPresent = selectedPlayerId === NOT_PRESENT;
  const isFirstUnidentified = currentUnidentifiedPos <= 0;
  const isLastUnidentified = currentUnidentifiedPos >= totalUnidentified - 1;
  const hasSelection = selectedPlayerId !== null;

  function handleNext() {
    if (isLastUnidentified) {
      onComplete(identifications as Record<number, PlayerSlot>);
    } else {
      // Jump to next unidentified segment
      const nextIdx = unidentifiedIndices[currentUnidentifiedPos + 1];
      if (nextIdx !== undefined) goTo(nextIdx);
    }
  }

  function handlePrev() {
    // Jump to previous unidentified segment
    const prevIdx = unidentifiedIndices[currentUnidentifiedPos - 1];
    if (prevIdx !== undefined) goTo(prevIdx);
  }

  const progressPercent =
    ((currentUnidentifiedPos + 1) / totalUnidentified) * 100;

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col gap-4 px-4 py-4">
      {/* Header */}
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold tracking-tight text-slate-800">
          {t('title')}
        </h1>
        <div className="flex items-center gap-3">
          <span className="shrink-0 text-sm leading-none text-slate-500">
            {t('blockProgress', {
              current: currentUnidentifiedPos + 1,
              total: totalUnidentified,
            })}
          </span>
          <div className="mt-0.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
            <div
              className="h-full rounded-full bg-sky-500 transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </div>

      {/* Segment cards with fade/slide animation */}
      <div
        className="flex min-h-0 flex-1 flex-col transition-all duration-150"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(8px)',
        }}
      >
        <SegmentIdentifier
          playerCrops={currentSegment.player_crops}
          selectedPlayerId={isNotPresent ? null : selectedPlayerId}
          onSelect={(playerId) => identify(animatingIndex, playerId)}
          disabledPlayerIds={disabledPlayersPerSegment[animatingIndex]}
        />

        {/* Not present option */}
        <button
          className={`mt-4 flex w-full items-center justify-center gap-2 rounded-xl border-2 px-4 py-3 text-sm font-medium transition-all duration-200 ${
            isNotPresent
              ? 'border-slate-700 bg-slate-50 text-slate-700'
              : 'border-transparent bg-slate-50/50 text-slate-400 opacity-70 hover:opacity-90'
          }`}
          onClick={() => identify(animatingIndex, NOT_PRESENT)}
        >
          <UserX className="h-4 w-4" />
          {t('notInBlock')}
        </button>
      </div>

      {/* Navigation */}
      <div className="mt-2 flex gap-3">
        <button
          className="min-h-[48px] flex-1 rounded-xl border border-slate-300 bg-transparent px-4 py-2 font-medium text-slate-700 transition-colors hover:bg-slate-100 disabled:opacity-40"
          onClick={handlePrev}
          disabled={isFirstUnidentified}
        >
          {t('back')}
        </button>
        <button
          className="min-h-[48px] flex-1 rounded-xl bg-slate-700 px-4 py-2 font-medium text-white transition-colors hover:bg-slate-800 disabled:opacity-40"
          onClick={handleNext}
          disabled={!hasSelection}
        >
          {isLastUnidentified ? t('seeReport') : t('next')}
        </button>
      </div>
    </div>
  );
};

export default PlayerIdentificationFlow;
